import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import {
  create_new_staff,
  get_staff_by_id,
  update_staff,
  delete_staff,
  get_all_staff
} from '../../api/staffAPI'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import Select from 'react-select'
import notify from '../../utils/IANotification';
import FileUploadField from '../../components/FileUploadField';
import SubNavbar from '../../components/ui/SubNavbar';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import {
  MdArrowBack,
  MdSave,
  MdDelete,
  MdAdd,
  MdSearch,
  MdNavigateBefore,
  MdNavigateNext,
  MdPerson
} from 'react-icons/md';

export default function StaffFormPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  useFetchUserData()

  const initialStaffId = new URLSearchParams(window.location.search).get("staff_id") || "new"
  const [currentStaffId, setCurrentStaffId] = useState(initialStaffId)

  const [activeTab, setActiveTab] = useState("general")
  const [allStaff, setAllStaff] = useState([])
  const [staffSearchQuery, setStaffSearchQuery] = useState("")
  const [loadingStaffList, setLoadingStaffList] = useState(true)
  const [loadingForm, setLoadingForm] = useState(false)

  const [staffFormData, setStaffFormData] = useState({
    name: "",
    role: "Staff",
    title: "",
    chinese_name: "",
    email: "",
    bio: "",
    seq_order: 0,
    media_asset_id: null
  })

  const roleOptions = [
    { value: "Pastor", label: "Pastor" },
    { value: "Staff", label: "Staff" }
  ]

  const tabs = [
    { id: 'general', label: 'Profile Details' },
    { id: 'photo', label: 'Profile Photo' },
  ]

  // Fetch all staff list for the left cycling sidebar
  const loadAllStaff = async () => {
    setLoadingStaffList(true)
    const res = await get_all_staff()
    if (res?.status === 200) {
      setAllStaff(res.data || [])
    }
    setLoadingStaffList(false)
  }

  // Fetch individual staff member by ID
  const fetchStaffById = async (idToFetch) => {
    const id = idToFetch || currentStaffId
    if (!id || id === "new") {
      setStaffFormData({
        name: "",
        role: "Staff",
        title: "",
        chinese_name: "",
        email: "",
        bio: "",
        seq_order: allStaff.length + 1,
        media_asset_id: null
      })
      return
    }

    setLoadingForm(true)
    const data = await get_staff_by_id(id)
    if (data?.status === 200) {
      setStaffFormData(data.data)
    } else {
      notify({ type: "error", message: "Failed to load staff details" })
    }
    setLoadingForm(false)
  }

  useEffect(() => {
    loadAllStaff()
  }, [])

  useEffect(() => {
    fetchStaffById(currentStaffId)
  }, [currentStaffId])

  // Switch to a different staff member
  const handleSelectStaff = (targetId) => {
    if (String(targetId) === String(currentStaffId)) return
    setCurrentStaffId(targetId)
    navigate(`/admin/staff/details?staff_id=${targetId}`, { replace: true })
  }

  // Cycle to previous or next staff member
  const handleCycleStaff = (direction) => {
    if (allStaff.length === 0) return
    const currentIndex = allStaff.findIndex(s => String(s.id) === String(currentStaffId))

    let nextIndex = 0
    if (currentIndex === -1) {
      nextIndex = direction > 0 ? 0 : allStaff.length - 1
    } else {
      nextIndex = (currentIndex + direction + allStaff.length) % allStaff.length
    }

    const nextStaff = allStaff[nextIndex]
    if (nextStaff) {
      handleSelectStaff(nextStaff.id)
    }
  }

  const saveStaff = async () => {
    if (!staffFormData.name?.trim()) {
      notify({ type: "error", message: "Staff name is required" })
      return
    }
    const data = await create_new_staff(staffFormData)
    if (data?.status === 200) {
      notify({ type: "success", message: "Staff member created successfully" })
      await loadAllStaff()
      handleSelectStaff(data.data.id)
    } else {
      notify({ type: "error", message: data?.message || "Failed to create staff member" })
    }
  }

  const updateStaffMember = async () => {
    if (!staffFormData.name?.trim()) {
      notify({ type: "error", message: "Staff name is required" })
      return
    }
    const data = await update_staff(currentStaffId, staffFormData)
    if (data?.status === 200) {
      notify({ type: "success", message: "Staff member updated successfully" })
      fetchStaffById(currentStaffId)
      loadAllStaff()
    } else {
      notify({ type: "error", message: data?.message || "Failed to update staff member" })
    }
  }

  const deleteStaffMember = async () => {
    if (!window.confirm("Are you sure you want to delete this staff member?")) return
    const data = await delete_staff(currentStaffId)
    if (data?.status === 200) {
      notify({ type: "success", message: "Staff member deleted successfully" })
      await loadAllStaff()
      navigate("/admin/staff")
    } else {
      notify({ type: "error", message: data?.message || "Failed to delete staff member" })
    }
  }

  // Filter staff for the left sidebar
  const filteredStaff = useMemo(() => {
    if (!staffSearchQuery.trim()) return allStaff
    const q = staffSearchQuery.toLowerCase()
    return allStaff.filter(s =>
      s.name?.toLowerCase().includes(q) ||
      s.chinese_name?.toLowerCase().includes(q) ||
      s.title?.toLowerCase().includes(q) ||
      s.role?.toLowerCase().includes(q)
    )
  }, [allStaff, staffSearchQuery])

  const currentStaffIndex = allStaff.findIndex(s => String(s.id) === String(currentStaffId))

  return (
    <AdminLayout
      navSelected={"Church Staff"}
      headerText={currentStaffId === "new" ? "New Staff Member" : "Edit Staff Member Profile"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/staff')}>
              Back to Staff List
            </Button>
            <h1 className='text-xl font-bold text-gray-900'>
              {currentStaffId === "new" ? "Create Staff Member" : staffFormData.name || "Edit Staff Member"}
            </h1>
          </div>
          <div className='flex items-center gap-2'>
            {currentStaffId !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deleteStaffMember}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (currentStaffId === "new") saveStaff()
              else updateStaffMember()
            }}>
              {currentStaffId === "new" ? "Create Staff" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 items-start">

        {/* ========================================================================= */}
        {/* LEFT SECTION: STAFF CYCLING & SELECTION SIDEBAR */}
        {/* ========================================================================= */}
        <aside className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm space-y-4 lg:sticky lg:top-20 max-h-[calc(100vh-6rem)] flex flex-col">

          {/* Sidebar Header & Quick Actions */}
          <div className="space-y-2 shrink-0">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
                <MdPerson className="text-base text-blue-600" />
                Staff Directory ({allStaff.length})
              </span>
              <button
                type="button"
                onClick={() => handleSelectStaff("new")}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                  currentStaffId === "new"
                    ? "bg-blue-600 text-white shadow-xs"
                    : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                }`}
              >
                <MdAdd size={14} /> New
              </button>
            </div>

            {/* Cycle Previous / Next Quick Buttons */}
            {allStaff.length > 1 && (
              <div className="flex items-center justify-between gap-2 p-1.5 bg-gray-50 rounded-xl border border-gray-200 text-xs">
                <button
                  type="button"
                  onClick={() => handleCycleStaff(-1)}
                  className="flex-1 inline-flex items-center justify-center gap-1 py-1 px-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100 font-semibold text-gray-700 shadow-xs transition"
                  title="Cycle to previous staff member"
                >
                  <MdNavigateBefore size={16} /> Prev
                </button>
                <span className="text-[11px] font-mono font-medium text-gray-500 px-1">
                  {currentStaffIndex !== -1 ? `${currentStaffIndex + 1} of ${allStaff.length}` : '-'}
                </span>
                <button
                  type="button"
                  onClick={() => handleCycleStaff(1)}
                  className="flex-1 inline-flex items-center justify-center gap-1 py-1 px-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-100 font-semibold text-gray-700 shadow-xs transition"
                  title="Cycle to next staff member"
                >
                  Next <MdNavigateNext size={16} />
                </button>
              </div>
            )}

            {/* Search filter input */}
            <div className="relative">
              <MdSearch className="absolute left-2.5 top-2.5 text-gray-400 text-base" />
              <input
                type="text"
                value={staffSearchQuery}
                onChange={(e) => setStaffSearchQuery(e.target.value)}
                placeholder="Search by name or role..."
                className="w-full text-xs pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
            </div>
          </div>

          {/* Scrollable Staff List to Cycle and Update */}
          <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 min-h-[160px] max-h-[55vh] divide-y divide-gray-50">
            {loadingStaffList ? (
              <div className="p-4 text-center text-xs text-gray-400">Loading staff directory...</div>
            ) : filteredStaff.length > 0 ? (
              filteredStaff.map((staff) => {
                const isSelected = String(staff.id) === String(currentStaffId)
                return (
                  <button
                    key={staff.id}
                    type="button"
                    onClick={() => handleSelectStaff(staff.id)}
                    className={`w-full text-left p-2.5 rounded-xl transition-all flex items-center justify-between gap-2.5 group ${
                      isSelected
                        ? "bg-blue-600 text-white shadow-sm ring-2 ring-blue-300"
                        : "hover:bg-gray-100 text-gray-800"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[10px] font-mono font-bold px-1 rounded ${
                          isSelected ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
                        }`}>
                          #{staff.seq_order || 0}
                        </span>
                        <span className="font-semibold text-xs truncate">
                          {staff.name || "Unnamed"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-[11px] mt-0.5 truncate">
                        {staff.chinese_name && (
                          <span className={isSelected ? "text-blue-100" : "text-gray-500"}>
                            {staff.chinese_name} ·
                          </span>
                        )}
                        <span className={isSelected ? "text-blue-200" : "text-gray-400"}>
                          {staff.title || staff.role}
                        </span>
                      </div>
                    </div>

                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                      isSelected
                        ? "bg-white/20 text-white"
                        : staff.role === "Pastor"
                        ? "bg-purple-100 text-purple-700"
                        : "bg-emerald-100 text-emerald-700"
                    }`}>
                      {staff.role}
                    </span>
                  </button>
                )
              })
            ) : (
              <div className="p-4 text-center text-xs text-gray-400">
                No staff members match '{staffSearchQuery}'
              </div>
            )}
          </div>
        </aside>

        {/* ========================================================================= */}
        {/* RIGHT SECTION: FORM DETAILS & PROFILE PHOTO */}
        {/* ========================================================================= */}
        <main className="space-y-4">
          <SubNavbar
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {/* TAB 1: PROFILE DETAILS */}
          {activeTab === "general" && (
            <Card
              title={currentStaffId === "new" ? "New Staff Member Information" : `Profile Details: ${staffFormData.name || "Staff Member"}`}
              subtitle="Configure official name, role category, display order, email, and detailed biographical write-up."
            >
              {loadingForm ? (
                <div className="p-8 text-center text-gray-400">Loading details...</div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="input-container">
                      <label className="input-label font-semibold">English Name</label>
                      <input
                        type="text"
                        placeholder='e.g. Rev Dr Aaron Tay'
                        className="text-input"
                        value={staffFormData.name || ""}
                        onChange={(e) => setStaffFormData({ ...staffFormData, name: e.target.value })}
                      />
                    </div>

                    <div className="input-container">
                      <label className="input-label font-semibold">Chinese Name (optional)</label>
                      <input
                        type="text"
                        placeholder='e.g. 郑韶强牧师'
                        className="text-input"
                        value={staffFormData.chinese_name || ""}
                        onChange={(e) => setStaffFormData({ ...staffFormData, chinese_name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="input-container">
                      <label className="input-label font-semibold">Staff Category Role</label>
                      <Select
                        options={roleOptions}
                        value={roleOptions.find(opt => opt.value === staffFormData.role)}
                        onChange={(e) => setStaffFormData({ ...staffFormData, role: e.value })}
                      />
                    </div>

                    <div className="input-container">
                      <label className="input-label font-semibold">Title / Position</label>
                      <input
                        type="text"
                        placeholder='e.g. Pastor-in-Charge, Lay Ministry Staff'
                        className="text-input"
                        value={staffFormData.title || ""}
                        onChange={(e) => setStaffFormData({ ...staffFormData, title: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="input-container">
                      <label className="input-label font-semibold">Email Address (optional)</label>
                      <input
                        type="email"
                        placeholder='e.g. staff@bmc.org.sg'
                        className="text-input"
                        value={staffFormData.email || ""}
                        onChange={(e) => setStaffFormData({ ...staffFormData, email: e.target.value })}
                      />
                    </div>

                    <div className="input-container">
                      <label className="input-label font-semibold">Display Sequence Order</label>
                      <input
                        type="number"
                        placeholder='e.g. 1'
                        className="text-input"
                        value={staffFormData.seq_order !== undefined ? staffFormData.seq_order : 0}
                        onChange={(e) => setStaffFormData({ ...staffFormData, seq_order: e.target.value })}
                      />
                      <p className="text-xs text-gray-500 mt-1">Lower numbers will be displayed first on the public staff page.</p>
                    </div>
                  </div>

                  <div className="input-container">
                    <label className="input-label font-semibold">Staff Write-up / Biography</label>
                    <textarea
                      rows={6}
                      placeholder="Share the staff member's background, testimony, ministry focus, or biographical write-up..."
                      className="text-input"
                      value={staffFormData.bio || ""}
                      onChange={(e) => setStaffFormData({ ...staffFormData, bio: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">This write-up will be showcased on the church public website with a 'Read More' modal preview.</p>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-100 gap-2">
                    <Button
                      variant="primary"
                      icon={MdSave}
                      onClick={() => {
                        if (currentStaffId === "new") saveStaff()
                        else updateStaffMember()
                      }}
                    >
                      {currentStaffId === "new" ? "Create Staff Member" : "Save Changes"}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* TAB 2: PROFILE PHOTO */}
          {activeTab === "photo" && (
            <Card title="Staff Member Photo" subtitle="Upload official portrait image displayed on church website.">
              {currentStaffId === "new" ? (
                <p className="text-xs text-gray-400 italic py-6 text-center">Save staff member profile first before uploading portrait photo.</p>
              ) : (
                <div className="space-y-6">
                  <FileUploadField
                    upload_url={config.endpoint + "/file/staff/id/" + currentStaffId}
                    max_files={1}
                    text={"Upload Profile Photo"}
                    subtext={"Upload portrait image for website staff listing"}
                    onChange={() => {
                      fetchStaffById(currentStaffId)
                      loadAllStaff()
                    }}
                  />
                </div>
              )}
            </Card>
          )}
        </main>
      </div>
    </AdminLayout>
  )
}
